import { type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import { CountryCode, type Currency, type Language, type Listing, type PricesV2, type SearchListing } from '@ev-tech/search-modules-api';

import { formatPriceWithDecimals, formatPriceWithoutDecimals } from './numberFormatters';

export const getRentPrice = (property: SearchListing, prices: PricesV2, language: Language, currency: Currency) => {
  const rentTotal = formatPriceWithoutDecimals(prices.rentTotal?.min || 0, language, currency);
  const rentNetPerSquareMeter = formatPriceWithDecimals(prices.rentNetPerSquareMeter?.min || 0, language, currency);
  const rentNet = formatPriceWithoutDecimals(prices.rentNet?.min || 0, language, currency);

  const utilitiesTotal = formatPriceWithDecimals(prices.rentUtilities?.min || 0, language, currency);

  type PriceLabelTranslationKey = Extract<SearchModuleTranslationKey, `search.price.label.${string}`>;

  const rentalPropertyProps: {
    value: string;
    rentNetValue: string;
    utilitiesValue: string;
    labelTranslationKey: PriceLabelTranslationKey;
  } = {
    value: rentTotal,
    rentNetValue: rentNet,
    utilitiesValue: utilitiesTotal,
    labelTranslationKey: 'search.price.label.rentTotal',
  };

  if (property.countryAlpha2 === CountryCode.DE && rentTotal && rentNet && utilitiesTotal) {
    rentalPropertyProps.labelTranslationKey = 'search.price.label.rentTotal';
  }

  if (property.countryAlpha2 === CountryCode.DE && !utilitiesTotal && !rentTotal) {
    rentalPropertyProps.labelTranslationKey = 'search.price.label.rentNet';
  }

  // other countries, this was originally here before the Germany-specific work was started but these translations need to be checked again
  if (!rentTotal && rentNetPerSquareMeter) {
    rentalPropertyProps.value = rentNetPerSquareMeter;
    rentalPropertyProps.rentNetValue = '';
    rentalPropertyProps.labelTranslationKey = 'search.price.label.rentNetPerSquareMeter';
  }

  if (!rentTotal && !rentNetPerSquareMeter && rentNet) {
    rentalPropertyProps.value = rentNet;
    rentalPropertyProps.rentNetValue = '';
    rentalPropertyProps.labelTranslationKey = 'search.price.label.rentNet';
  }

  if (property.hasPriceOnRequest || (!rentNet && !rentTotal && !rentNetPerSquareMeter)) {
    rentalPropertyProps.value = '';
    rentalPropertyProps.labelTranslationKey = 'search.price.label.rent';
  }

  return rentalPropertyProps;
};

export const getParsedShopName = (shopName?: string): string => {
  if (!shopName) {
    return '';
  }
  const regexName = /Engel & Völkers/g;
  const replacedShopName = shopName.replaceAll(regexName, '');

  return replacedShopName.trim();
};

export const mapShopNamesFromResults = (results: Listing[] | SearchListing[]) => [
  ...new Set(results.filter((property) => property.shopName).map((property) => getParsedShopName(property.shopName))),
];
